import { Company } from 'module/api/company/types';
import { useTranslate } from 'module/hooks/use-translation';
import { Loading } from 'module/ui/components/atoms/loading';
import { SignalType } from 'api/signal/types';
import {
  SignalSummaryValue,
  SignalSummaryData,
} from 'api/signal-occurrences/types';
import moment from 'moment';
import { useAccountMetaQuery } from 'queries/account';
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { MonthTOC } from './month-toc';
import { useSummaryData } from './use-summary-data';
import {
  GroupByType,
  getPeriodKeyFromItem,
  getPeriodStart,
  SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX,
} from './utils';

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
  justify-content: flex-start;
  align-self: flex-start;
  overflow-y: auto;
  /* Match timeline: sticky period labels sit below sticky header row */
  --signals-v2-period-sticky-top: 56px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: var(--spacing-05) 0;
  position: sticky;
  top: 0;
  background-color: var(--background-primary);
  z-index: 5;
  margin-bottom: var(--spacing-08);
  box-shadow: 0px 5px 10px 0px #ffffff;
`;

const TocSidebar = styled.div`
  position: sticky;
  left: var(--spacing-04);
  padding: var(--spacing-04);
  z-index: var(--zindex-elevated);
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  height: 100%;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding: var(--spacing-08) var(--spacing-08);
  position: relative;
  gap: 0;
`;

const MonthSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  gap: var(--spacing-04);
  overflow: visible;
  width: 100%;
  padding-left: 0;
  margin-bottom: var(--spacing-10);
  border-bottom: 1px solid var(--divider-default);
`;

const MonthLabelColumn = styled.div`
  flex-shrink: 0;
  width: 7.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const MonthContentColumn = styled.div`
  flex: 0 1 ${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px;
  max-width: ${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Spacer = styled.div<{ height: number }>`
  height: ${(props) => props.height}px;
  flex-shrink: 0;
`;

const MonthLabelLeft = styled.div`
  position: sticky;
  top: var(--signals-v2-period-sticky-top, 0px);
  align-self: flex-start;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--gray-40);
  white-space: nowrap;
  width: 100%;
  max-width: var(--layout-06);
  text-align: right;
  padding-right: var(--spacing-04);
  z-index: 2;
  pointer-events: none;
  background-color: var(--background-primary);
  padding-top: var(--spacing-01);
  padding-bottom: var(--spacing-02);
`;

const SectionContainer = styled.div`
  margin-bottom: var(--spacing-10);
  width: 100%;
  display: block;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const SectionTitle = styled.div`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-05);
`;

const CrispSummaryBox = styled.div<{ signalType: SignalType }>`
  background-color: ${(props) => {
    if (props.signalType === SignalType.Risk) {
      return 'var(--red-10)';
    }
    if (props.signalType === SignalType.Opportunity) {
      return 'var(--green-10)';
    }
    if (props.signalType === SignalType.Operational) {
      return 'var(--gray-10)';
    }

    return 'var(--gray-10)';
  }};
  border-radius: var(--border-radius-large);
  padding: var(--spacing-04);
  margin-bottom: var(--spacing-04);
  color: var(--text-primary);
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DetailedSummaryList = styled.ul`
  list-style: disc;
  padding-left: var(--spacing-06);
  margin: 0;
  margin-bottom: var(--spacing-06);
`;

const DetailedSummaryItem = styled.li`
  margin-bottom: var(--spacing-04);
  font-size: var(--font-size-base);
  color: var(--text-primary);
  line-height: 1.6;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DetailedSummaryTitle = styled.strong`
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
`;

const DetailedSummaryContent = styled.span`
  color: var(--text-primary);
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-08);
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-08);
  min-height: 200px;
  color: var(--text-secondary);
  font-size: var(--font-size-lg);
`;

const SectionEmptyState = styled.div`
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  padding: var(--spacing-04) 0;
`;

const MonthEmptyState = styled.div`
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  padding: var(--spacing-06) 0;
  text-align: center;
`;

const FullPageLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-primary);
  z-index: var(--zindex-layer-00);
  pointer-events: all;
  overflow: hidden;
`;

const TopLoadingIndicator = styled(LoadingIndicator)`
  position: sticky;
  top: 0;
  background-color: var(--background-primary);
  z-index: 5;
`;

const BottomLoadingIndicator = styled(LoadingIndicator)`
  position: sticky;
  bottom: 0;
  background-color: var(--background-primary);
  z-index: 5;
`;

const Sentinel = styled.div`
  height: 1px;
  width: 100%;
  pointer-events: none;
  visibility: hidden;
`;

interface SignalsV2SummaryViewProps {
  company: Company;
}

const ITEMS_ABOVE = 5; // Top 5 elements
const ITEMS_BELOW = 15; // Bottom 15 elements (increased to fill viewport)
const ESTIMATED_ITEM_HEIGHT = 600; // Estimated height per month section

export const SignalsV2SummaryView: React.FC<SignalsV2SummaryViewProps> = ({
  company,
}) => {
  const translate = useTranslate();
  // Summary view always uses MONTH grouping, regardless of timeline view settings
  const [groupBy] = useState<GroupByType>('MONTH');

  // Fetch billing start date from account meta
  const { data: accountMeta } = useAccountMetaQuery();
  const billingStartDate = useMemo(() => {
    if (accountMeta?.company?.createdAt) {
      // createdAt is a timestamp in milliseconds
      return moment(accountMeta.company.createdAt).format('YYYY-MM-DD');
    }

    // Fallback to default if not available
    return '2020-04-01';
  }, [accountMeta]);

  const {
    data,
    selectedPeriodKey,
    isInitializing,
    isLoadingSelectedPeriod,
    isLoadingDown,
    isLoadingUp,
    handlePeriodSelect,
    loadEarlierPeriods,
    loadLaterPeriods,
  } = useSummaryData({
    companyId: company.companyId,
    groupBy,
    billingStartDate,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportIndex, setViewportIndex] = useState<number>(0);
  const [itemHeights, setItemHeights] = useState<Map<number, number>>(
    new Map(),
  );
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const lastScrollTop = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  // Track if we're currently updating heights to prevent ResizeObserver loops
  const isUpdatingHeightsRef = useRef(false);

  // Update container height when it changes (debounced to prevent scroll glitches)
  useEffect(() => {
    const updateContainerHeight = () => {
      // Don't update if we're currently measuring item heights (prevents loops)
      if (isUpdatingHeightsRef.current) {
        return;
      }

      if (containerRef.current) {
        const newHeight = containerRef.current.clientHeight;

        // Always set initial height, then only update if changed significantly
        setContainerHeight((prevHeight) => {
          // If this is the initial measurement (prevHeight is 0), always set it
          if (prevHeight === 0 && newHeight > 0) {
            return newHeight;
          }

          // Only update if height changed significantly (more than 50px) to prevent frequent updates
          if (Math.abs(newHeight - prevHeight) > 50) {
            return newHeight;
          }

          return prevHeight;
        });
      }
    };

    // Initial measurement - use multiple attempts to ensure we get the height
    updateContainerHeight();

    // Also try after a short delay in case container isn't ready yet
    const initialTimeout = setTimeout(() => {
      updateContainerHeight();
    }, 100);

    // Debounce resize observer updates
    let resizeTimeout: NodeJS.Timeout | null = null;
    const resizeObserver = new ResizeObserver(() => {
      // Don't react to resize if we're updating heights
      if (isUpdatingHeightsRef.current) {
        return;
      }

      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        updateContainerHeight();
        resizeTimeout = null;
      }, 300); // Increased debounce to prevent loops
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  // Calculate visible range
  // If there are few items, render all of them. Otherwise use virtualized rendering
  const visibleRange = useMemo(() => {
    // If we have fewer items than the visible window, render all
    if (data.length <= ITEMS_ABOVE + ITEMS_BELOW + 1) {
      return { start: 0, end: data.length };
    }

    // Calculate how many items we need to fill the viewport
    // Use a stable calculation that doesn't change too frequently
    // If containerHeight is not set yet, use a reasonable default (window height or large buffer)
    const defaultViewportHeight =
      typeof window !== 'undefined' ? window.innerHeight * 0.8 : 1000; // Fallback to 1000px if window is not available

    const effectiveHeight =
      containerHeight > 0 ? containerHeight : defaultViewportHeight;
    const itemsNeededForViewport =
      Math.ceil(effectiveHeight / ESTIMATED_ITEM_HEIGHT) + 5;

    const itemsBelow = Math.max(ITEMS_BELOW, itemsNeededForViewport);
    const start = Math.max(0, viewportIndex - ITEMS_ABOVE);
    const end = Math.min(data.length, viewportIndex + itemsBelow + 1);

    return { start, end };
  }, [viewportIndex, data.length, containerHeight]);

  // Calculate total height of items before visible range
  const topSpacerHeight = useMemo(() => {
    let height = 0;

    for (let i = 0; i < visibleRange.start; i++) {
      height += itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
    }

    return height;
  }, [visibleRange.start, itemHeights]);

  // Calculate total height of items after visible range
  const bottomSpacerHeight = useMemo(() => {
    let height = 0;

    for (let i = visibleRange.end; i < data.length; i++) {
      height += itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
    }

    return height;
  }, [visibleRange.end, data.length, itemHeights]);

  // Measure item heights - debounced to prevent render loops
  useEffect(() => {
    // Use a timeout to debounce height measurements
    const measurementTimeout = setTimeout(() => {
      isUpdatingHeightsRef.current = true;

      const newHeights = new Map(itemHeights);
      let hasChanges = false;

      itemRefs.current.forEach((element, index) => {
        if (element) {
          const height = element.offsetHeight;
          const currentHeight = newHeights.get(index);

          // Only update if height changed significantly (more than 10px) to prevent micro-updates
          if (
            currentHeight === undefined ||
            Math.abs(height - currentHeight) > 10
          ) {
            newHeights.set(index, height);
            hasChanges = true;
          }
        }
      });

      // Only update if there are significant changes
      if (hasChanges) {
        setItemHeights(new Map(newHeights));
      }

      // Reset flag after a short delay to allow layout to settle
      setTimeout(() => {
        isUpdatingHeightsRef.current = false;
      }, 50);
    }, 150); // Debounce measurements

    return () => {
      clearTimeout(measurementTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, visibleRange]);

  // Handle scroll to update viewport index
  const handleScroll = useCallback(() => {
    if (!containerRef.current || isScrolling.current) {
      return;
    }

    const container = containerRef.current;
    const { scrollTop } = container;
    const currentScrollTop = scrollTop;

    lastScrollTop.current = currentScrollTop;

    // Debounce viewport index calculation to prevent scroll glitches
    if (viewportIndexUpdateTimeoutRef.current) {
      clearTimeout(viewportIndexUpdateTimeoutRef.current);
    }

    viewportIndexUpdateTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current || isScrolling.current) {
        return;
      }

      // Calculate which item should be in viewport based on scroll position
      // Start from the beginning to find the correct item, not just visible range
      let accumulatedHeight = 0;
      let newViewportIndex = 0;
      const latestScrollTop = containerRef.current.scrollTop;
      const latestClientHeight = containerRef.current.clientHeight;

      // If we're at the very top (scrollTop is 0 or very small), use index 0
      if (latestScrollTop <= 10) {
        newViewportIndex = 0;
      } else {
        for (let i = 0; i < data.length; i++) {
          const itemHeight = itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
          const itemTop = accumulatedHeight;
          const itemBottom = accumulatedHeight + itemHeight;

          // Check if viewport center (scrollTop + clientHeight/2) is within this item
          const viewportCenter = latestScrollTop + latestClientHeight / 2;

          if (viewportCenter >= itemTop && viewportCenter < itemBottom) {
            newViewportIndex = i;
            break;
          }

          // If we've passed the viewport center, this is the item
          if (viewportCenter < itemTop) {
            newViewportIndex = Math.max(0, i - 1);
            break;
          }

          accumulatedHeight += itemHeight;
        }

        // Only use last item if we're actually at the bottom of the scroll
        const latestScrollHeight = containerRef.current.scrollHeight;
        const distanceFromBottom =
          latestScrollHeight - latestScrollTop - latestClientHeight;

        if (
          newViewportIndex === 0 &&
          accumulatedHeight > 0 &&
          distanceFromBottom < 10
        ) {
          newViewportIndex = data.length - 1;
        }
      }

      // Update viewport index if it changed
      // Mark as user scrolling to prevent programmatic scroll interference
      if (newViewportIndex !== viewportIndex) {
        // Set flag before updating to prevent scroll effect from running
        isUserScrollingRef.current = true;
        previousViewportIndexRef.current = viewportIndex; // Keep old value for comparison
        setViewportIndex(newViewportIndex);

        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Reset flag after user stops scrolling (debounce)
        scrollTimeoutRef.current = setTimeout(() => {
          isUserScrollingRef.current = false;
          scrollTimeoutRef.current = null;
        }, 200);
      }
    }, 50); // Debounce viewport index updates

    // Note: Loading is now handled by Intersection Observer below
  }, [data.length, itemHeights, viewportIndex]);

  // Setup Intersection Observer for loading more data
  useEffect(() => {
    const container = containerRef.current;
    const topSentinel = topSentinelRef.current;
    const bottomSentinel = bottomSentinelRef.current;

    if (!container || !topSentinel || !bottomSentinel) {
      return;
    }

    // Disconnect existing observer
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
    }

    // Create new Intersection Observer
    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Bottom sentinel visible - load earlier periods (scroll down)
            if (entry.target === bottomSentinel) {
              if (!isLoadingDown) {
                loadEarlierPeriods();
              }
            }
            // Top sentinel visible - load later periods (scroll up)
            else if (entry.target === topSentinel) {
              if (!isLoadingUp) {
                loadLaterPeriods();
              }
            }
          }
        });
      },
      {
        root: container,
        rootMargin: '200px', // Trigger when 200px away from viewport
        threshold: 0,
      },
    );

    // Observe both sentinels
    intersectionObserverRef.current.observe(topSentinel);
    intersectionObserverRef.current.observe(bottomSentinel);

    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
        intersectionObserverRef.current = null;
      }
    };
  }, [
    loadEarlierPeriods,
    loadLaterPeriods,
    isLoadingDown,
    isLoadingUp,
    data.length,
  ]);

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      lastScrollTop.current = container.scrollTop;

      return () => {
        container.removeEventListener('scroll', handleScroll);
        // Clean up scroll timeouts
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        if (viewportIndexUpdateTimeoutRef.current) {
          clearTimeout(viewportIndexUpdateTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  // Helper to get period key from item
  const getPeriodKeyFromItemLocal = useCallback(
    (item: SignalSummaryValue) => {
      return getPeriodKeyFromItem(item, groupBy);
    },
    [groupBy],
  );

  // Find index of selected period in data
  const findSelectedPeriodIndex = useCallback(() => {
    if (!selectedPeriodKey || data.length === 0) {
      return -1;
    }

    return data.findIndex(
      (item) => getPeriodKeyFromItemLocal(item) === selectedPeriodKey,
    );
  }, [data, selectedPeriodKey, getPeriodKeyFromItemLocal]);

  // Track if viewport index change is from user scroll or programmatic
  const isUserScrollingRef = useRef<boolean>(false);
  const previousViewportIndexRef = useRef<number>(viewportIndex);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewportIndexUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track previous selected month key to detect actual changes
  const previousSelectedMonthKeyRef = useRef<string>('');
  const previousLoadingStateRef = useRef<boolean>(false);

  // Track previous data length to detect when new data is loaded
  const previousDataLengthRef = useRef<number>(0);

  // Set selected month as first viewport element and scroll to it
  useEffect(() => {
    // Don't update viewport while loading
    if (isLoadingSelectedPeriod) {
      previousLoadingStateRef.current = true;

      return;
    }

    // Track when loading completes
    const justFinishedLoading = previousLoadingStateRef.current;

    previousLoadingStateRef.current = false;

    // Don't update if no month selected or no data
    if (!selectedPeriodKey || data.length === 0 || !containerRef.current) {
      if (selectedPeriodKey) {
        previousSelectedMonthKeyRef.current = selectedPeriodKey;
      }

      return;
    }

    // Always scroll to selected month when:
    // 1. Month key changed (always scroll when selection changes)
    // 2. Just finished loading (only if the selected month is in the newly loaded data)
    // Don't scroll when data length changes due to pagination - only when month selection changes
    const monthKeyChanged =
      selectedPeriodKey !== previousSelectedMonthKeyRef.current;
    const shouldScroll = justFinishedLoading || monthKeyChanged;

    if (!shouldScroll) {
      return;
    }

    // Reset user scrolling flag when period selection changes to allow scrolling
    if (monthKeyChanged) {
      isUserScrollingRef.current = false;
      // Clear any pending scroll timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    } else if (isUserScrollingRef.current) {
      // Don't scroll if user is actively scrolling and period hasn't changed
      return;
    }

    const selectedIndex = findSelectedPeriodIndex();

    // Scroll to selected month and set it as viewport start
    if (selectedIndex >= 0) {
      // Update viewport index to show selected month as first visible item
      // Set scrolling flag to prevent scroll handler from interfering
      isScrolling.current = true;
      setViewportIndex(selectedIndex);
      previousViewportIndexRef.current = selectedIndex;
      previousSelectedMonthKeyRef.current = selectedPeriodKey;
      previousDataLengthRef.current = data.length;

      // Function to scroll to the selected month
      const scrollToSelectedMonth = () => {
        if (!containerRef.current) {
          return;
        }

        // Try to find element by data-section-id first (more reliable)
        const sectionId = selectedPeriodKey.replace('-', ' ');
        const targetElement = containerRef.current.querySelector(
          `[data-section-id="${sectionId}"]`,
        ) as HTMLElement;

        if (targetElement) {
          // Use scrollIntoView for more reliable scrolling
          isScrolling.current = true;

          // Calculate position relative to container
          const containerRect = containerRef.current.getBoundingClientRect();
          const elementRect = targetElement.getBoundingClientRect();
          const relativeTop =
            elementRect.top -
            containerRect.top +
            containerRef.current.scrollTop;

          // Scroll to position
          containerRef.current.scrollTop = relativeTop;
          lastScrollTop.current = relativeTop;

          // Reset scrolling flag after animation
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isScrolling.current = false;
            });
          });
        } else {
          // Fallback: Calculate scroll position using item heights
          let accumulatedHeight = 0;

          for (let i = 0; i < selectedIndex; i++) {
            accumulatedHeight += itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
          }

          isScrolling.current = true;
          containerRef.current.scrollTop = accumulatedHeight;
          lastScrollTop.current = accumulatedHeight;

          // Reset scrolling flag after animation
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isScrolling.current = false;
            });
          });
        }
      };

      // Wait a bit for DOM to update if we just finished loading
      // Use longer delay to account for debounced height measurements
      if (justFinishedLoading || monthKeyChanged) {
        // Use setTimeout to ensure DOM is updated and heights are measured
        setTimeout(() => {
          scrollToSelectedMonth();
        }, 200); // Increased delay to allow height measurements to complete
      } else {
        scrollToSelectedMonth();
      }
    } else {
      // Month not found in data yet, but update tracking
      previousSelectedMonthKeyRef.current = selectedPeriodKey;
      previousDataLengthRef.current = data.length;
    }
  }, [
    selectedPeriodKey,
    isLoadingSelectedPeriod,
    findSelectedPeriodIndex,
    itemHeights,
    data.length,
  ]);

  // Generic helper to get period label based on groupBy
  const getPeriodLabel = (item: SignalSummaryValue) => {
    const periodKey = getPeriodKeyFromItemLocal(item);

    if (!periodKey) {
      return '';
    }

    const periodStart = getPeriodStart(periodKey, groupBy);

    switch (groupBy) {
      case 'WEEK': {
        // Format: "Dec 1 - Dec 7"
        const weekStart = periodStart.clone().startOf('week');
        const weekEnd = periodStart.clone().endOf('week');

        // If same month, show "MMM D - D"
        if (weekStart.month() === weekEnd.month()) {
          return `${weekStart.format('MMM D')} - ${weekEnd.format('D')}`;
        }

        // If different months, show "MMM D - MMM D"
        return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`;
      }
      case 'MONTH':
      default:
        return periodStart.format('MMM YYYY');
    }
  };

  // Generic helper to get period key based on groupBy (uses WeakMap)
  const getPeriodKey = (item: SignalSummaryValue) => {
    return getPeriodKeyFromItemLocal(item);
  };

  const getSignalTypeFromTitle = (title: string): SignalType => {
    if (title.toLowerCase().includes('risk')) {
      return SignalType.Risk;
    }
    if (title.toLowerCase().includes('opportunity')) {
      return SignalType.Opportunity;
    }
    if (title.toLowerCase().includes('operational')) {
      return SignalType.Operational;
    }

    return SignalType.Risk; // Default to Risk
  };

  // Helper function to check if section has data
  const hasSectionData = (summaryData?: SignalSummaryData): boolean => {
    if (!summaryData) {
      return false;
    }

    const hasCrispSummary = !!summaryData.crispSummary;
    const hasDetailedSummary =
      summaryData.detailedSummary &&
      typeof summaryData.detailedSummary === 'object' &&
      Object.keys(summaryData.detailedSummary).length > 0;

    return hasCrispSummary || hasDetailedSummary;
  };

  const renderSection = (title: string, summaryData?: SignalSummaryData) => {
    const signalType = getSignalTypeFromTitle(title);

    if (!summaryData) {
      return null;
    }

    // Check if data is empty
    const hasCrispSummary = !!summaryData?.crispSummary;
    const hasDetailedSummary =
      summaryData?.detailedSummary &&
      typeof summaryData?.detailedSummary === 'object' &&
      Object.keys(summaryData?.detailedSummary).length > 0;
    const isEmpty = !hasCrispSummary && !hasDetailedSummary;

    return (
      <SectionContainer>
        <SectionTitle>{title} - Summary</SectionTitle>
        {isEmpty ? (
          <SectionEmptyState>No data available</SectionEmptyState>
        ) : (
          <>
            {summaryData.crispSummary && (
              <CrispSummaryBox signalType={signalType}>
                {summaryData.crispSummary}
              </CrispSummaryBox>
            )}
            {summaryData.detailedSummary &&
              typeof summaryData.detailedSummary === 'object' &&
              Object.keys(summaryData.detailedSummary).length > 0 && (
                <DetailedSummaryList>
                  {Object.values(summaryData.detailedSummary).map(
                    (item, index) => (
                      <DetailedSummaryItem key={index}>
                        <DetailedSummaryTitle>
                          {item.title}
                        </DetailedSummaryTitle>
                        {' - '}
                        <DetailedSummaryContent>
                          {item.content}
                        </DetailedSummaryContent>
                      </DetailedSummaryItem>
                    ),
                  )}
                </DetailedSummaryList>
              )}
          </>
        )}
      </SectionContainer>
    );
  };

  // Get the first period key for TOC
  const firstPeriodKey = data.length > 0 ? getPeriodKey(data[0]) : '';

  return (
    <>
      {(groupBy === 'MONTH' || groupBy === 'WEEK') && (
        <TocSidebar>
          <MonthTOC
            groupBy={groupBy}
            onMonthSelect={handlePeriodSelect}
            selectedMonthKey={selectedPeriodKey || firstPeriodKey}
            billingStartDate={billingStartDate}
            title={translate('accounts.page.signals_v2.toc.time_period')}
          />
        </TocSidebar>
      )}
      <SummaryWrapper ref={containerRef}>
        {!isLoadingSelectedPeriod && <HeaderSection />}
        <SummaryContainer>
          {/* Show full page loader when summary API is in progress (initial load or selected period fetch) */}
          {(isInitializing || isLoadingSelectedPeriod) && (
            <FullPageLoader>
              <Loading size="large" />
            </FullPageLoader>
          )}
          {!isInitializing && !isLoadingSelectedPeriod && (
            <>
              {data.length === 0 ? (
                <EmptyState>No data available</EmptyState>
              ) : (
                <>
                  {/* Top loading indicator for scroll up */}
                  {isLoadingUp && (
                    <TopLoadingIndicator>
                      <Loading size="small" />
                    </TopLoadingIndicator>
                  )}

                  {/* Top sentinel for Intersection Observer */}
                  <Sentinel ref={topSentinelRef} />

                  {/* Top spacer for items before visible range */}
                  {topSpacerHeight > 0 && <Spacer height={topSpacerHeight} />}

                  {/* Render only visible items */}
                  {data
                    .slice(visibleRange.start, visibleRange.end)
                    .map((item, relativeIndex) => {
                      const absoluteIndex = visibleRange.start + relativeIndex;
                      const periodLabel = getPeriodLabel(item);
                      const periodKey = getPeriodKey(item);

                      return (
                        <MonthSection
                          key={periodKey || absoluteIndex}
                          data-section-id={
                            periodKey ? periodKey.replace('-', ' ') : undefined
                          }
                          ref={(el) => {
                            if (el) {
                              itemRefs.current.set(absoluteIndex, el);
                            } else {
                              itemRefs.current.delete(absoluteIndex);
                            }
                          }}
                        >
                          <MonthLabelColumn>
                            <MonthLabelLeft>{periodLabel}</MonthLabelLeft>
                          </MonthLabelColumn>
                          <MonthContentColumn>
                            {!hasSectionData(item.Risk) &&
                            !hasSectionData(item.Opportunity) &&
                            !hasSectionData(item.Operational) ? (
                              <MonthEmptyState>
                                No data available
                              </MonthEmptyState>
                            ) : (
                              <>
                                {renderSection('Risks', item.Risk)}
                                {renderSection('Opportunity', item.Opportunity)}
                                {renderSection('Operational', item.Operational)}
                              </>
                            )}
                          </MonthContentColumn>
                        </MonthSection>
                      );
                    })}

                  {/* Bottom spacer for items after visible range */}
                  {bottomSpacerHeight > 0 && (
                    <Spacer height={bottomSpacerHeight} />
                  )}

                  {/* Bottom sentinel for Intersection Observer */}
                  <Sentinel ref={bottomSentinelRef} />

                  {/* Bottom loading indicator for scroll down */}
                  {isLoadingDown && (
                    <BottomLoadingIndicator>
                      <Loading size="small" />
                    </BottomLoadingIndicator>
                  )}
                </>
              )}
            </>
          )}
        </SummaryContainer>
      </SummaryWrapper>
    </>
  );
};
